package com.milta.web

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.milta.core.localization.LocalStrings
import com.milta.feature.auth.domain.entity.OAuthProvider
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.alignItems
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.height
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.position
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

private data class WebLanguage(val tag: String, val label: String, val flagFile: String)

private val webLanguages = listOf(
    WebLanguage("fr", "Français", "flags_flag_fr.png"),
    WebLanguage("nl", "Nederlands", "flags_flag_nl.png"),
    WebLanguage("en", "English", "flags_flag_en.png"),
    WebLanguage("ro", "Română", "flags_flag_ro.png"),
    WebLanguage("bg", "Български", "flags_flag_bg.png"),
    WebLanguage("sq", "Shqip", "flags_flag_sq.png"),
    WebLanguage("pl", "Polski", "flags_flag_pl.png"),
    WebLanguage("it", "Italiano", "flags_flag_it.png"),
    WebLanguage("es", "Español", "flags_flag_es.png"),
)

// Same hex values as shared/src/commonMain/kotlin/com/milta/core/ui/theme/Color.kt (Milta charte
// graphique — Background/TextPrimary/TextSecondary/Surface/Error) — kept as plain CSS strings
// here since Compose HTML's typed Color/rgb() API differs from Compose UI's, and the property()
// escape hatch is the one guaranteed-stable surface.
private const val BACKGROUND = "#EEF2F5"
private const val SURFACE = "#FFFFFF"
private const val TEXT_PRIMARY = "#1E3A4A"
private const val TEXT_SECONDARY = "#6B8A9A"
private const val ERROR = "#D32F2F"
private const val BORDER = "#D7DFE3"

/**
 * Web-native login screen, Compose HTML (real DOM). Reworked 2026-08-20 to match the MOBILE
 * `WelcomeScreen` (`shared/.../feature/auth/presentation/WelcomeScreen.kt`) structurally — same
 * colors ([BACKGROUND]/[TEXT_PRIMARY]/[TEXT_SECONDARY]/[SURFACE]/[ERROR] mirror
 * `core/ui/theme/Color.kt`'s `Background`/`TextPrimary`/`TextSecondary`/`Surface`/`Error`), not the
 * webApp dashboard's own card/border convention: no split panel, no bordered card — content floats
 * directly on the plain background, centered both horizontally AND vertically, in the exact same
 * order as mobile (logo -> title -> tagline -> error -> Google button -> Apple button), with the
 * language switcher as a compact flag + dropdown pinned top-right (mobile's `DropdownMenu`), not a
 * row of chips. Same underlying logic (`onOAuthSignIn`, language switching) as before.
 */
@Composable
fun WebWelcomeScreen(
    selectedLanguage: String,
    onLanguageChange: (String) -> Unit,
    onOAuthSignIn: (OAuthProvider) -> Unit,
    errorMessage: String?,
) {
    val strings = LocalStrings.current
    var loadingProvider by remember { mutableStateOf<OAuthProvider?>(null) }

    LaunchedEffect(errorMessage) {
        if (errorMessage != null) loadingProvider = null
    }

    Div(
        attrs = {
            style {
                width(100.percent)
                property("min-height", "100vh")
                property("background", BACKGROUND)
                position(Position.Relative)
                property("box-sizing", "border-box")
            }
        },
    ) {
        Div(attrs = { style { position(Position.Absolute); property("top", "16px"); property("right", "16px") } }) {
            LanguagePicker(selectedLanguage = selectedLanguage, onLanguageChange = onLanguageChange)
        }

        // Main content — centered both ways, same as mobile's Column(fillMaxSize, Center/Center).
        Div(
            attrs = {
                style {
                    width(100.percent)
                    property("min-height", "100vh")
                    property("display", "flex")
                    property("flex-direction", "column")
                    alignItems(AlignItems.Center)
                    property("justify-content", "center")
                    padding(32.px)
                    property("box-sizing", "border-box")
                }
            },
        ) {
            Div(attrs = { style { width(360.px); property("max-width", "100%"); property("display", "flex"); property("flex-direction", "column"); alignItems(AlignItems.Center) } }) {
                Img(
                    src = drawableUri("milta_full_logo.png"),
                    alt = strings.common.appName,
                    attrs = { style { height(56.px) } },
                )

                Div(attrs = { style { height(48.px) } })

                Div(
                    attrs = {
                        style {
                            property("color", TEXT_PRIMARY)
                            property("font-size", "26px")
                            property("font-weight", "700")
                            property("text-align", "center")
                        }
                    },
                ) { Text(strings.common.welcomeTitle) }

                Div(attrs = { style { height(12.px) } })

                Div(
                    attrs = {
                        style {
                            property("color", TEXT_SECONDARY)
                            property("font-size", "15px")
                            property("text-align", "center")
                        }
                    },
                ) { Text(strings.common.welcomeSubtitle) }

                Div(attrs = { style { height(40.px) } })

                if (errorMessage != null) {
                    Div(
                        attrs = {
                            style {
                                property("color", ERROR)
                                property("font-size", "13px")
                                property("text-align", "center")
                            }
                        },
                    ) { Text(errorMessage) }
                    Div(attrs = { style { height(16.px) } })
                }

                WebOAuthButton(
                    text = strings.common.continueWithGoogle,
                    logoFile = "logo_google.png",
                    isLoading = loadingProvider == OAuthProvider.GOOGLE,
                    enabled = loadingProvider == null,
                    onClick = {
                        loadingProvider = OAuthProvider.GOOGLE
                        onOAuthSignIn(OAuthProvider.GOOGLE)
                    },
                )

                Div(attrs = { style { height(16.px) } })

                WebOAuthButton(
                    text = strings.common.continueWithApple,
                    logoFile = "logo_apple.png",
                    isLoading = loadingProvider == OAuthProvider.APPLE,
                    enabled = loadingProvider == null,
                    onClick = {
                        loadingProvider = OAuthProvider.APPLE
                        onOAuthSignIn(OAuthProvider.APPLE)
                    },
                )
            }
        }
    }
}

// Mobile's Google/Apple buttons: white bg, 16dp radius, subtle elevation — mirrored here as a
// light box-shadow (Compose HTML has no elevation concept, box-shadow is the equivalent).
@Composable
private fun WebOAuthButton(
    text: String,
    logoFile: String,
    isLoading: Boolean,
    enabled: Boolean,
    onClick: () -> Unit,
) {
    Button(
        attrs = {
            if (enabled) onClick { onClick() }
            style {
                width(100.percent)
                height(52.px)
                property("border-radius", "16px")
                property("border", "none")
                property("box-shadow", "0 1px 3px rgba(30,58,74,0.16)")
                property("background", SURFACE)
                property("color", TEXT_PRIMARY)
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "center")
                property("gap", "10px")
                property("font-size", "15px")
                property("font-weight", "700")
                property("cursor", if (enabled) "pointer" else "default")
                property("opacity", if (enabled) "1" else "0.6")
                property("box-sizing", "border-box")
            }
        },
    ) {
        if (isLoading) {
            Span(attrs = { style { property("font-size", "13px") } }) { Text("…") }
        } else {
            Img(src = drawableUri(logoFile), alt = "", attrs = { style { height(20.px); width(20.px) } })
        }
        Text(text)
    }
}

// Mirrors mobile's top-right flag + DropdownMenu exactly (plain list, no search field — unlike
// WebSettingsScreen's fuller LanguageDropdown, which is a dashboard-only pattern).
@Composable
private fun LanguagePicker(selectedLanguage: String, onLanguageChange: (String) -> Unit) {
    var isOpen by remember { mutableStateOf(false) }
    val current = webLanguages.firstOrNull { it.tag == selectedLanguage } ?: webLanguages.first()

    Div(attrs = { style { position(Position.Relative) } }) {
        Img(
            src = drawableUri(current.flagFile),
            alt = current.label,
            attrs = {
                onClick { isOpen = !isOpen }
                style {
                    width(26.px); height(18.px)
                    property("border", "1px solid $BORDER"); property("border-radius", "2px")
                    property("cursor", "pointer")
                }
            },
        )

        if (isOpen) {
            Div(
                attrs = {
                    style {
                        position(Position.Absolute)
                        property("top", "calc(100% + 6px)"); property("right", "0")
                        property("background", SURFACE); property("border-radius", "10px")
                        property("box-shadow", "0 12px 28px rgba(30,58,74,0.18)")
                        property("z-index", "50"); property("overflow", "hidden"); property("min-width", "160px")
                    }
                },
            ) {
                webLanguages.forEach { lang ->
                    Div(
                        attrs = {
                            onClick { onLanguageChange(lang.tag); isOpen = false }
                            style {
                                property("display", "flex"); property("align-items", "center"); property("gap", "10px")
                                padding(9.px, 14.px); property("cursor", "pointer")
                                property("border-bottom", "1px solid $BORDER")
                                property("font-size", "13px"); property("color", TEXT_PRIMARY)
                            }
                        },
                    ) {
                        Img(src = drawableUri(lang.flagFile), alt = lang.label, attrs = { style { width(22.px); height(15.px); property("border", "1px solid $BORDER"); property("border-radius", "2px") } })
                        Text(lang.label)
                    }
                }
            }
        }
    }
}
